import proc1 from "@/assets/proc-1.webp";
import proc2 from "@/assets/proc-2.webp";
import proc3 from "@/assets/proc-3.webp";
import proc4 from "@/assets/proc-4.webp";
import proc5 from "@/assets/proc-5.webp";
import proc6 from "@/assets/proc-6.webp";
import proc7 from "@/assets/proc-7.webp";

export type Block = {
  h: string;
  p?: string[];
  list?: string[];
};

export type FaqItemData = { q: string; a: string };

export type RelatedLink = { label: string; to: string; text?: string; image?: string };

export type ProcedureContent = {
  slug: string;
  path: string;
  name: string;
  h1: string;
  heroTitle: string;
  lead: string;
  metaTitle: string;
  metaDescription: string;
  keyword: string;
  cardText: string;
  tags: string[];
  image: string;
  blocks: Block[];
  related: RelatedLink[];
  articles?: { label: string; to: string }[];
  faq: FaqItemData[];
};

const P = (slug: string) => `/especialidades/${slug}`;

export const PROCEDURES: ProcedureContent[] = [
  {
    slug: "mommy-makeover-foz-do-iguacu",
    path: P("mommy-makeover-foz-do-iguacu"),
    name: "Mommy makeover",
    h1: "Mommy makeover em Foz do Iguaçu",
    heroTitle: "Cirurgia pós-gestação com plano personalizado",
    lead:
      "O mommy makeover reúne procedimentos que podem tratar mudanças nas mamas, no abdômen e no contorno corporal após a gestação. A combinação não é padronizada: ela é definida de acordo com a anatomia, as queixas, o momento de vida e as condições de saúde de cada paciente.",
    metaTitle: "Mommy makeover em Foz do Iguaçu | Dr. Kelson",
    metaDescription:
      "Entenda como funciona o mommy makeover, quais procedimentos podem ser combinados e como é o planejamento com o Dr. Kelson em Foz do Iguaçu.",
    keyword: "mommy makeover em Foz do Iguaçu",
    cardText: "Procedimentos combinados para restaurar contornos após a gestação.",
    tags: ["Mamas", "Abdômen", "Contorno corporal"],
    image: proc1,
    blocks: [
      {
        h: "O que é o mommy makeover",
        p: [
          "Mommy makeover é o nome usado para um **plano cirúrgico** que combina dois ou mais procedimentos em uma mesma estratégia. Os mais comuns envolvem cirurgia das mamas, abdominoplastia e lipoaspiração ou lipoescultura.",
          "A proposta não é “voltar ao corpo de antes”, mas avaliar quais mudanças incomodam a paciente e quais podem ser abordadas com **segurança**, respeitando limites anatômicos e **expectativas realistas**.",
        ],
      },
      {
        h: "Quando a avaliação pode fazer sentido",
        list: [
          "Flacidez e excesso de pele no abdômen.",
          "Afastamento da musculatura abdominal, quando presente.",
          "Queda, perda de volume ou excesso de volume nas mamas.",
          "Gordura localizada e perda de definição corporal.",
          "Mudanças corporais após gestação e amamentação que permanecem mesmo com estabilidade de peso.",
        ],
      },
      {
        h: "Quais procedimentos podem fazer parte",
        p: [
          "A combinação é construída caso a caso. Entre as possibilidades estão abdominoplastia, mamoplastia de aumento, mastopexia com ou sem implantes, mamoplastia redutora e lipoescultura. Nem todos os procedimentos precisam ser realizados juntos.",
        ],
        list: [
          "Abdominoplastia para excesso de pele e flacidez abdominal.",
          "Cirurgia das mamas para ajustar volume, posição e formato.",
          "Lipoescultura para aprimorar o contorno em áreas selecionadas.",
        ],
      },
      {
        h: "Como é feito o planejamento",
        p: [
          "Na **primeira consulta**, o Dr. Kelson avalia histórico clínico, gestações, amamentação, variações de peso, cicatrizes anteriores, qualidade da pele e objetivos. Também conversa sobre riscos, limites, cicatrizes, estrutura hospitalar e apoio necessário na recuperação.",
          "Em alguns casos, dividir o plano em etapas pode ser mais seguro ou adequado. Essa decisão é individual e faz parte do planejamento.",
        ],
      },
      {
        h: "Recuperação e pós-operatório",
        p: [
          "Por envolver mais de uma área, o mommy makeover costuma exigir organização prévia da rotina e apoio em casa nos primeiros dias. O uso de cintas, sutiã cirúrgico, medicações, cuidados com curativos e retorno gradual às atividades é orientado conforme a combinação realizada.",
          "O tempo de internação, o nível de desconforto e a volta ao trabalho variam. Atividades físicas e esforços só devem ser retomados após liberação médica.",
        ],
      },
      {
        h: "Resultados e expectativas",
        p: [
          "O resultado evolui ao longo dos meses, conforme o inchaço diminui e os tecidos se acomodam. Cicatrizes também passam por etapas de maturação. Manter peso estável e seguir as orientações do **pós-operatório** contribui para uma evolução mais previsível.",
        ],
      },
      {
        h: "Por que realizar uma avaliação com o Dr. Kelson",
        p: [
          "O atendimento é individualizado e inclui escuta atenta, exame físico e construção de um plano realista. O consultório está localizado no centro de Foz do Iguaçu, em um edifício moderno, com estacionamento privativo e sala de espera confortável.",
        ],
      },
    ],
    related: [
      { label: "Abdominoplastia", to: P("abdominoplastia-foz-do-iguacu") },
      { label: "Lipoescultura", to: P("lipoescultura-foz-do-iguacu") },
      { label: "Mastopexia com implantes", to: P("mastopexia-com-implantes-foz-do-iguacu") },
    ],
    articles: [
      { label: "Mommy makeover: a cirurgia que restaura a autoestima da mamãe", to: "/mommy-makeover-conheca-a-cirurgia-que-restaura-a-autoestima-da-mamae" },
      { label: "Como se planejar para realizar uma cirurgia plástica", to: "/como-se-planejar-para-realizar-uma-cirurgia-plastica-em-2024" },
      { label: "Pré-operatório: o que saber antes da cirurgia", to: "/pre-operatorio" },
    ],
    faq: [
      {
        q: "Quais cirurgias podem ser combinadas no mommy makeover?",
        a: "As combinações mais comuns envolvem abdômen, mamas e contorno corporal, mas a escolha depende da avaliação e dos critérios de **segurança**.",
      },
      {
        q: "Quanto tempo depois da amamentação posso fazer a cirurgia?",
        a: "É importante aguardar a estabilização das mamas, do peso e das condições gerais de saúde. O intervalo adequado deve ser definido após avaliação médica.",
      },
      {
        q: "É possível ter outra gestação depois do mommy makeover?",
        a: "Uma nova gestação pode acontecer, mas pode modificar o resultado obtido. O planejamento deve considerar os planos reprodutivos da paciente.",
      },
      {
        q: "O pós-operatório é muito doloroso?",
        a: "A percepção de dor varia entre pessoas e conforme a combinação de procedimentos. O controle do desconforto faz parte do plano **pós-operatório**.",
      },
      {
        q: "Quanto custa o mommy makeover?",
        a: "O valor depende das cirurgias associadas, da estrutura necessária, da equipe e da complexidade do caso. O orçamento é definido após a **consulta**.",
      },
    ],
  },
  {
    slug: "abdominoplastia-foz-do-iguacu",
    path: P("abdominoplastia-foz-do-iguacu"),
    name: "Abdominoplastia",
    h1: "Abdominoplastia em Foz do Iguaçu",
    heroTitle: "Contorno abdominal com avaliação individualizada",
    lead:
      "A abdominoplastia é uma cirurgia voltada ao tratamento do excesso de pele e da flacidez do abdômen. O planejamento considera proporções corporais, qualidade da pele, musculatura, cicatrizes anteriores e objetivos realistas.",
    metaTitle: "Abdominoplastia em Foz do Iguaçu | Dr. Kelson",
    metaDescription:
      "Saiba para quem a abdominoplastia pode ser indicada, como funciona o planejamento e como é a recuperação com o Dr. Kelson em Foz do Iguaçu.",
    keyword: "abdominoplastia em Foz do Iguaçu",
    cardText: "Remove excessos abdominais e redefine o contorno corporal.",
    tags: ["Excesso de pele", "Flacidez", "Pós-gestação"],
    image: proc2,
    blocks: [
      {
        h: "O que é a abdominoplastia",
        p: [
          "A cirurgia remove excesso de pele da região abdominal e pode incluir a correção da parede muscular quando há afastamento dos músculos. O procedimento não é uma cirurgia para emagrecimento e costuma ser mais bem planejado quando o peso está estável.",
        ],
      },
      {
        h: "Queixas que podem levar à avaliação",
        list: [
          "Excesso de pele no abdômen.",
          "Flacidez após gestação ou grande perda de peso.",
          "Alteração do contorno abdominal que não melhora apenas com exercício.",
          "Afastamento da musculatura abdominal, quando identificado no exame.",
          "Desconforto com dobras de pele e dificuldade de adaptação de roupas.",
        ],
      },
      {
        h: "Abdominoplastia e lipoaspiração",
        p: [
          "Em casos selecionados, a abdominoplastia pode ser associada à lipoaspiração para melhorar o contorno de áreas próximas. A associação depende da anatomia, da **segurança** do tempo cirúrgico e da condição clínica da paciente.",
        ],
      },
      {
        h: "Cicatriz e posicionamento",
        p: [
          "A abdominoplastia deixa uma cicatriz na parte inferior do abdômen, geralmente planejada para ficar coberta por roupas íntimas ou biquíni. A extensão varia conforme a quantidade de pele a ser tratada e a **técnica** escolhida.",
          "A qualidade final da cicatriz depende de fatores individuais, cuidados **pós-operatórios** e evolução ao longo dos meses.",
        ],
      },
      {
        h: "Recuperação",
        p: [
          "Nos primeiros dias, é comum haver inchaço, sensação de tensão e necessidade de caminhar com cautela. O retorno ao trabalho depende do tipo de atividade e da evolução individual. Cinta, curativos, medicações e restrição de esforços são orientados pela equipe.",
          "A retomada de exercícios acontece de forma gradual, sempre após liberação médica.",
        ],
      },
      {
        h: "Planejamento com segurança",
        p: [
          "A **consulta** inclui avaliação do histórico de saúde, peso, gestações, cirurgias anteriores, tabagismo, uso de medicamentos e condições que possam interferir na cicatrização. Também são discutidos riscos, limites e expectativas.",
        ],
      },
    ],
    related: [
      { label: "Mommy makeover", to: P("mommy-makeover-foz-do-iguacu") },
      { label: "Lipoescultura", to: P("lipoescultura-foz-do-iguacu") },
      { label: "Mamoplastia de aumento", to: P("mamoplastia-de-aumento-foz-do-iguacu") },
    ],
    articles: [
      { label: "Abdominoplastia: entenda o procedimento", to: "/abdominoplastia-foz" },
      { label: "Cirurgias de contorno corporal são tendência", to: "/lipoaspiracao-e-cirurgias-de-contorno-corporal-sao-tendencia-no-brasil-e-no-mundo" },
      { label: "Pós-operatório: cuidados essenciais", to: "/pos-operatorio" },
    ],
    faq: [
      {
        q: "Abdominoplastia emagrece?",
        a: "Não é uma cirurgia de emagrecimento. Ela trata principalmente excesso de pele, flacidez e alterações da parede abdominal. Mudanças de peso podem modificar o resultado.",
      },
      {
        q: "A cicatriz fica muito grande?",
        a: "A extensão depende da quantidade de pele e da **técnica**. O planejamento busca posicioná-la em uma área baixa, mas toda cirurgia envolve cicatriz.",
      },
      {
        q: "Quanto tempo dura a internação?",
        a: "O tempo varia de acordo com o caso, a **técnica**, a associação com outros procedimentos e a evolução pós-operatória.",
      },
      {
        q: "Quando posso voltar ao trabalho?",
        a: "Atividades leves podem ser retomadas antes de trabalhos com esforço físico. A previsão é individual e deve ser confirmada pelo cirurgião.",
      },
      {
        q: "Quanto custa a abdominoplastia?",
        a: "O valor depende do planejamento, da estrutura hospitalar, da equipe e de eventuais associações. É definido após **avaliação presencial**.",
      },
    ],
  },
  {
    slug: "lipoescultura-foz-do-iguacu",
    path: P("lipoescultura-foz-do-iguacu"),
    name: "Lipoescultura",
    h1: "Lipoescultura em Foz do Iguaçu",
    heroTitle: "Contorno corporal com equilíbrio e proporção",
    lead:
      "A lipoescultura é planejada para tratar depósitos de gordura localizada e melhorar a relação entre diferentes áreas do corpo. O objetivo é aprimorar o contorno, respeitando anatomia, qualidade da pele e limites de **segurança**.",
    metaTitle: "Lipoescultura em Foz do Iguaçu | Dr. Kelson",
    metaDescription:
      "Entenda como a lipoescultura pode remodelar o contorno corporal, como é o planejamento e a recuperação com o Dr. Kelson em Foz do Iguaçu.",
    keyword: "lipoescultura em Foz do Iguaçu",
    cardText: "Modela a silhueta com mais definição e harmonia.",
    tags: ["Gordura localizada", "Contorno", "Proporção"],
    image: proc3,
    blocks: [
      {
        h: "O que é lipoescultura",
        p: [
          "A lipoescultura utiliza **técnica**s de lipoaspiração para remover gordura de áreas selecionadas e, quando indicado, pode incluir a utilização dessa gordura para ajustar volume em outras regiões. O plano varia conforme o corpo e os objetivos da paciente.",
          "O procedimento não substitui perda de peso, alimentação equilibrada ou atividade física. Ele é voltado à modelagem de áreas que permanecem desproporcionais mesmo com hábitos saudáveis.",
        ],
      },
      {
        h: "Áreas que podem ser avaliadas",
        list: [
          "Abdômen e cintura.",
          "Flancos e região lombar.",
          "Costas.",
          "Braços.",
          "Coxas.",
          "Papada e outras áreas de pequeno volume, quando indicadas.",
        ],
      },
      {
        h: "Quem pode ser candidata",
        p: [
          "Em geral, a avaliação é mais favorável quando a pessoa está com peso relativamente estável, possui boa condição de saúde e entende que o resultado depende da qualidade da pele e da resposta individual. Flacidez importante pode exigir outra abordagem cirúrgica.",
        ],
      },
      {
        h: "Como é o planejamento",
        p: [
          "O Dr. Kelson analisa o contorno como um conjunto, e não apenas uma área isolada. Fotografias, exame físico e conversa sobre prioridades ajudam a definir onde retirar gordura, quais regiões não devem ser tratadas e se existe **indicação** de associação com outro procedimento.",
        ],
      },
      {
        h: "Recuperação",
        p: [
          "Inchaço, manchas roxas e sensibilidade são comuns nas primeiras semanas. O uso de cinta e outros cuidados é definido pela equipe. A volta ao trabalho varia conforme a extensão do procedimento e o esforço exigido na rotina.",
          "O resultado aparece progressivamente, com redução do edema e acomodação dos tecidos ao longo dos meses.",
        ],
      },
      {
        h: "Resultados realistas",
        p: [
          "A lipoescultura pode melhorar curvas e proporções, mas não cria um corpo padronizado. O planejamento busca coerência com a anatomia e com uma aparência natural, sem prometer uma silhueta específica.",
        ],
      },
    ],
    related: [
      { label: "Abdominoplastia", to: P("abdominoplastia-foz-do-iguacu") },
      { label: "Mommy makeover", to: P("mommy-makeover-foz-do-iguacu") },
      { label: "Mamoplastia de aumento", to: P("mamoplastia-de-aumento-foz-do-iguacu") },
    ],
    articles: [
      { label: "Lipoaspiração: entenda o procedimento", to: "/lipoaspiracao-foz" },
      { label: "Cirurgias de contorno corporal são tendência", to: "/lipoaspiracao-e-cirurgias-de-contorno-corporal-sao-tendencia-no-brasil-e-no-mundo" },
      { label: "Por que o emagrecimento rápido não é indicado no pré-operatório", to: "/por-que-o-emagrecimento-rapido-nao-e-indicado-em-um-pre-operatorio-de-cirurgia-plastica" },
    ],
    faq: [
      {
        q: "Lipoescultura emagrece?",
        a: "Não. A proposta é remodelar o contorno corporal. Pacientes que desejam perda importante de peso precisam de outro plano de cuidado.",
      },
      {
        q: "Qual a diferença entre lipoaspiração e lipoescultura?",
        a: "A lipoaspiração descreve a retirada de gordura. O termo lipoescultura costuma ser usado quando o planejamento considera o contorno global e, em alguns casos, a redistribuição de gordura.",
      },
      {
        q: "A pele fica flácida depois?",
        a: "A resposta depende da elasticidade da pele, da quantidade retirada e da anatomia. Quando existe flacidez importante, pode ser necessário avaliar outra cirurgia.",
      },
      {
        q: "Quando posso voltar a trabalhar?",
        a: "Depende da extensão, das áreas tratadas e do tipo de atividade. O retorno é gradual e individual.",
      },
      {
        q: "Quanto custa a lipoescultura?",
        a: "O valor é definido depois da avaliação, conforme áreas tratadas, tempo cirúrgico, estrutura e equipe necessárias.",
      },
    ],
  },
  {
    slug: "mamoplastia-de-aumento-foz-do-iguacu",
    path: P("mamoplastia-de-aumento-foz-do-iguacu"),
    name: "Mamoplastia de aumento",
    h1: "Mamoplastia de aumento em Foz do Iguaçu",
    heroTitle: "Volume mamário com planejamento de proporções",
    lead:
      "A mamoplastia de aumento utiliza implantes para aumentar ou recuperar o volume das mamas. A escolha não é feita apenas por tamanho: formato, projeção, largura do tórax, qualidade da pele e expectativas entram no planejamento.",
    metaTitle: "Prótese de mama em Foz do Iguaçu | Dr. Kelson",
    metaDescription:
      "Conheça o planejamento da mamoplastia de aumento com prótese, opções de volume, cicatriz e recuperação com o Dr. Kelson em Foz do Iguaçu.",
    keyword: "prótese de mama em Foz do Iguaçu",
    cardText: "Aumenta o volume das mamas com equilíbrio estético.",
    tags: ["Prótese", "Volume", "Proporção"],
    image: proc4,
    blocks: [
      {
        h: "Para quem a cirurgia pode ser avaliada",
        list: [
          "Pacientes que desejam aumentar o volume das mamas.",
          "Perda de volume após gestação, amamentação ou emagrecimento.",
          "Assimetria mamária que pode ser abordada cirurgicamente.",
          "Desejo de maior projeção ou preenchimento do colo, dentro dos limites anatômicos.",
        ],
      },
      {
        h: "Como escolher o implante",
        p: [
          "A escolha do implante considera medidas do tórax e das mamas, espessura dos tecidos, qualidade da pele, formato do implante e resultado desejado. Fotografias de referência podem ajudar na conversa, mas não substituem a avaliação anatômica.",
          "O objetivo é encontrar uma opção compatível com o corpo, evitando decisões baseadas apenas em volume ou tendência.",
        ],
      },
      {
        h: "Posição do implante e cicatriz",
        p: [
          "Existem diferentes planos para posicionar o implante e diferentes vias de acesso. A decisão depende das características da paciente e da **técnica** considerada mais adequada. A cicatriz é inevitável e sua localização é explicada antes da cirurgia.",
        ],
      },
      {
        h: "Quando o implante não resolve sozinho",
        p: [
          "Quando existe queda importante das mamas ou excesso de pele, apenas aumentar o volume pode não produzir o efeito desejado. Nesses casos, pode ser indicada uma mastopexia, com ou sem implantes.",
        ],
      },
      {
        h: "Recuperação e acompanhamento",
        p: [
          "O **pós-operatório** costuma incluir sutiã cirúrgico, controle de movimentos dos braços, medicações e retornos programados. A volta ao trabalho depende da atividade exercida.",
          "Implantes mamários exigem **acompanhamento** médico ao longo do tempo. Não existe garantia de que permanecerão sem necessidade de nova avaliação ou intervenção durante toda a vida.",
        ],
      },
      {
        h: "Expectativas e resultado",
        p: [
          "O resultado passa por acomodação gradual, e as mamas podem parecer mais altas ou firmes no início. Inchaço e mudanças de sensibilidade podem ocorrer. A evolução deve ser acompanhada nas **consulta**s de retorno.",
        ],
      },
    ],
    related: [
      { label: "Mastopexia com implantes", to: P("mastopexia-com-implantes-foz-do-iguacu") },
      { label: "Mamoplastia redutora", to: P("mamoplastia-redutora-foz-do-iguacu") },
      { label: "Mommy makeover", to: P("mommy-makeover-foz-do-iguacu") },
    ],
    articles: [
      { label: "Prótese de silicone: entenda o procedimento", to: "/protese-de-silicone-foz" },
      { label: "Próteses de silicone prejudicam o rastreio do câncer de mama?", to: "/proteses-de-silicone-prejudicam-o-rastreio-do-cancer-de-mama" },
      { label: "Quando há indicação para cirurgias mamárias sem prótese", to: "/quando-ha-indicacao-para-cirurgias-mamarias-sem-o-uso-de-protese" },
    ],
    faq: [
      {
        q: "Como escolher o tamanho da prótese?",
        a: "A decisão considera medidas, tecidos, estilo de vida e objetivo. O volume é uma parte do planejamento, não o único critério.",
      },
      {
        q: "A prótese de mama dura para sempre?",
        a: "Implantes precisam de **acompanhamento** e podem exigir substituição ou nova cirurgia em algum momento, conforme evolução clínica e exames.",
      },
      {
        q: "A prótese corrige a queda das mamas?",
        a: "Nem sempre. Quando há excesso de pele ou queda importante, pode ser necessária mastopexia.",
      },
      {
        q: "Quando posso voltar ao trabalho?",
        a: "Depende do tipo de atividade e da evolução. Trabalhos com esforço de braços podem exigir mais tempo.",
      },
      {
        q: "Quanto custa colocar prótese de mama?",
        a: "O orçamento depende do implante, da **técnica**, da estrutura hospitalar e da equipe. É definido após avaliação.",
      },
    ],
  },
  {
    slug: "mastopexia-com-implantes-foz-do-iguacu",
    path: P("mastopexia-com-implantes-foz-do-iguacu"),
    name: "Mastopexia com implantes",
    h1: "Mastopexia com implantes em Foz do Iguaçu",
    heroTitle: "Elevação das mamas associada à reposição de volume",
    lead:
      "A mastopexia reposiciona as mamas e remove excesso de pele. Quando há desejo ou necessidade de recuperar volume, o procedimento pode ser associado a implantes, sempre com planejamento individualizado.",
    metaTitle: "Mastopexia com implantes em Foz do Iguaçu",
    metaDescription:
      "Entenda como a mastopexia com implantes eleva as mamas, repõe volume e como é o planejamento com o Dr. Kelson em Foz do Iguaçu.",
    keyword: "mastopexia com implantes em Foz do Iguaçu",
    cardText: "Eleva as mamas e associa volume aos implantes.",
    tags: ["Elevação", "Volume", "Formato"],
    image: proc5,
    blocks: [
      {
        h: "O que a mastopexia pode tratar",
        list: [
          "Queda das mamas.",
          "Excesso de pele.",
          "Alteração de formato após gestação, amamentação ou perda de peso.",
          "Perda de volume no polo superior.",
          "Assimetria de posição ou formato, dentro das possibilidades cirúrgicas.",
        ],
      },
      {
        h: "Mastopexia com ou sem implantes",
        p: [
          "Nem toda mastopexia precisa de implante. Em algumas pacientes, o próprio tecido mamário pode ser reorganizado. Em outras, o implante ajuda a recuperar volume ou projeção. A escolha depende da anatomia, da qualidade dos tecidos e do resultado desejado.",
        ],
      },
      {
        h: "Cicatrizes",
        p: [
          "Para elevar as mamas, é necessário remover pele e reposicionar tecidos, o que gera cicatrizes. O desenho pode variar conforme o grau de queda e a **técnica**. Antes da cirurgia, o Dr. Kelson explica o posicionamento esperado e as etapas de maturação.",
        ],
      },
      {
        h: "Planejamento do volume",
        p: [
          "O implante deve ser compatível com a largura do tórax, a espessura dos tecidos e a capacidade de sustentação da pele. Volumes excessivos podem aumentar peso e tensão sobre os tecidos, por isso a decisão precisa equilibrar desejo e **segurança**.",
        ],
      },
      {
        h: "Recuperação",
        p: [
          "O **pós-operatório** inclui sutiã cirúrgico, cuidados com curativos, controle de movimentos e retornos. Inchaço e alterações temporárias de sensibilidade podem ocorrer. A volta ao trabalho e aos exercícios é gradual.",
        ],
      },
      {
        h: "Evolução dos resultados",
        p: [
          "As mamas mudam de posição e formato durante os primeiros meses. Cicatrizes amadurecem ao longo do tempo. Variações de peso, gestação e envelhecimento podem alterar o resultado no futuro.",
        ],
      },
    ],
    related: [
      { label: "Mamoplastia de aumento", to: P("mamoplastia-de-aumento-foz-do-iguacu") },
      { label: "Mamoplastia redutora", to: P("mamoplastia-redutora-foz-do-iguacu") },
      { label: "Mommy makeover", to: P("mommy-makeover-foz-do-iguacu") },
    ],
    articles: [
      { label: "Quando há indicação para cirurgias mamárias sem prótese", to: "/quando-ha-indicacao-para-cirurgias-mamarias-sem-o-uso-de-protese" },
      { label: "Prótese de silicone: entenda o procedimento", to: "/protese-de-silicone-foz" },
      { label: "Pós-operatório: cuidados essenciais", to: "/pos-operatorio" },
    ],
    faq: [
      {
        q: "Qual a diferença entre mastopexia e prótese de mama?",
        a: "A mastopexia trata a queda e o excesso de pele. A prótese aumenta ou repõe volume. Os procedimentos podem ser combinados quando indicado.",
      },
      {
        q: "A mastopexia sempre precisa de implante?",
        a: "Não. A necessidade depende do volume existente, da qualidade dos tecidos e do objetivo da paciente.",
      },
      {
        q: "Como ficam as cicatrizes?",
        a: "O formato varia conforme a **técnica** e o grau de queda. A localização é explicada no planejamento e a evolução depende da cicatrização individual.",
      },
      {
        q: "Quanto tempo leva para voltar ao trabalho?",
        a: "Depende da atividade profissional e da evolução. O retorno deve ser gradual e orientado pela equipe.",
      },
      {
        q: "Quanto custa a mastopexia com implantes?",
        a: "O valor depende do implante, da técnica, da estrutura e da complexidade. O orçamento é individualizado após **consulta**.",
      },
    ],
  },
  {
    slug: "mamoplastia-redutora-foz-do-iguacu",
    path: P("mamoplastia-redutora-foz-do-iguacu"),
    name: "Mamoplastia redutora",
    h1: "Mamoplastia redutora em Foz do Iguaçu",
    heroTitle: "Redução das mamas com foco em proporção e conforto",
    lead:
      "A mamoplastia redutora diminui o volume das mamas e remodela sua forma. O planejamento busca equilíbrio com o tórax e o corpo, considerando sintomas, expectativas, qualidade da pele e **segurança**.",
    metaTitle: "Mamoplastia redutora em Foz do Iguaçu",
    metaDescription:
      "Saiba como funciona a mamoplastia redutora, cicatrizes, recuperação e planejamento com o Dr. Kelson Kawamura em Foz do Iguaçu.",
    keyword: "mamoplastia redutora em Foz do Iguaçu",
    cardText: "Reduz o volume mamário, priorizando conforto e proporção.",
    tags: ["Redução", "Conforto", "Proporção"],
    image: proc6,
    blocks: [
      {
        h: "Quando a cirurgia pode ser considerada",
        list: [
          "Mamas volumosas e desproporcionais ao corpo.",
          "Desconforto nos ombros, costas ou pescoço associado ao peso mamário.",
          "Marcas causadas pelas alças do sutiã.",
          "Dificuldade para praticar atividades físicas.",
          "Assaduras ou irritação na região abaixo das mamas.",
          "Insatisfação com formato, queda ou assimetria.",
        ],
      },
      {
        h: "Como funciona",
        p: [
          "A cirurgia remove parte do tecido mamário, gordura e pele, reposicionando a aréola e remodelando a mama. A quantidade retirada e o formato final dependem da anatomia e do plano definido na **consulta**.",
        ],
      },
      {
        h: "Cicatrizes",
        p: [
          "A mamoplastia redutora deixa cicatrizes cuja extensão varia conforme o volume e o grau de queda. O desenho é explicado antes da cirurgia. As cicatrizes passam por mudanças de cor e textura durante o processo de maturação.",
        ],
      },
      {
        h: "Volume e proporção",
        p: [
          "A escolha do volume final precisa equilibrar o desejo da paciente, a base mamária e a viabilidade **técnica**. Não é possível garantir uma medida exata de sutiã, porque tamanhos variam entre marcas e o corpo passa por acomodação.",
        ],
      },
      {
        h: "Recuperação",
        p: [
          "Sutiã cirúrgico, medicações, curativos e limitação de esforços fazem parte do **pós-operatório**. A volta ao trabalho depende da atividade. Movimentos amplos e exercícios são retomados gradualmente.",
        ],
      },
      {
        h: "Resultados ao longo do tempo",
        p: [
          "O resultado inicial muda com a redução do inchaço e a acomodação das mamas. Gravidade, envelhecimento, gestações e variações de peso podem influenciar o formato no futuro.",
        ],
      },
    ],
    related: [
      { label: "Mastopexia com implantes", to: P("mastopexia-com-implantes-foz-do-iguacu") },
      { label: "Mamoplastia de aumento", to: P("mamoplastia-de-aumento-foz-do-iguacu") },
      { label: "Abdominoplastia", to: P("abdominoplastia-foz-do-iguacu") },
    ],
    articles: [
      { label: "Mamoplastia redutora: entenda o procedimento", to: "/mamoplastia-redutora-foz" },
      { label: "Quando há indicação para cirurgias mamárias sem prótese", to: "/quando-ha-indicacao-para-cirurgias-mamarias-sem-o-uso-de-protese" },
      { label: "Pré-operatório: o que saber antes da cirurgia", to: "/pre-operatorio" },
    ],
    faq: [
      {
        q: "A mamoplastia redutora pode aliviar dores?",
        a: "Algumas pacientes relatam melhora de desconfortos relacionados ao peso das mamas, mas cada caso precisa de avaliação e não há garantia de resolução de sintomas.",
      },
      {
        q: "É possível escolher o tamanho final?",
        a: "O objetivo pode ser discutido, mas não é possível garantir numeração exata de sutiã. A decisão considera proporção e **segurança**.",
      },
      {
        q: "Como ficam as cicatrizes?",
        a: "A extensão varia conforme **técnica** e volume retirado. O cuidado e a evolução individual influenciam o aspecto final.",
      },
      {
        q: "Quando posso voltar ao trabalho?",
        a: "Atividades leves podem ser retomadas antes de trabalhos físicos. O prazo é individual e orientado nos retornos.",
      },
      {
        q: "Quanto custa a mamoplastia redutora?",
        a: "O orçamento depende da complexidade, da **técnica**, da estrutura e da equipe. É definido após avaliação.",
      },
    ],
  },
  {
    slug: "rinoplastia-foz-do-iguacu",
    path: P("rinoplastia-foz-do-iguacu"),
    name: "Rinoplastia",
    h1: "Rinoplastia em Foz do Iguaçu",
    heroTitle: "Cirurgia do nariz com foco em equilíbrio facial",
    lead:
      "A rinoplastia modifica estruturas do nariz para tratar queixas de formato, proporção e, em casos selecionados, aspectos funcionais. O planejamento considera o nariz em relação ao rosto, sem buscar um modelo padronizado.",
    metaTitle: "Rinoplastia em Foz do Iguaçu | Dr. Kelson",
    metaDescription:
      "Entenda como funciona a rinoplastia, o planejamento do formato nasal, a recuperação e a avaliação com o Dr. Kelson em Foz do Iguaçu.",
    keyword: "rinoplastia em Foz do Iguaçu",
    cardText: "Refina o nariz em equilíbrio com o rosto.",
    tags: ["Harmonia facial", "Formato nasal", "Função"],
    image: proc7,
    blocks: [
      {
        h: "Quais queixas podem ser avaliadas",
        list: [
          "Dorso nasal alto ou irregular.",
          "Ponta nasal larga, caída ou pouco definida.",
          "Nariz desproporcional ao conjunto da face.",
          "Assimetria ou alterações após trauma.",
          "Dificuldade respiratória que precisa de avaliação funcional.",
        ],
      },
      {
        h: "Planejamento facial",
        p: [
          "A **consulta** inclui análise frontal, de perfil e da base nasal, além de fotografias e conversa sobre expectativas. O objetivo é entender o que incomoda e quais mudanças são tecnicamente possíveis sem descaracterizar o rosto.",
        ],
      },
      {
        h: "Estética e função",
        p: [
          "Quando existe queixa respiratória, o planejamento pode exigir avaliação de septo, válvulas nasais e outras estruturas. A possibilidade de abordagem funcional deve ser confirmada no exame e, quando necessário, com exames complementares ou avaliação associada.",
        ],
      },
      {
        h: "Como é a recuperação",
        p: [
          "Inchaço e manchas roxas podem ocorrer, especialmente nos primeiros dias. Curativos externos ou internos variam conforme a **técnica**. A rotina social pode ser retomada gradualmente, mas o nariz continua mudando por meses.",
          "Atividades físicas, exposição solar e uso de óculos podem exigir restrições temporárias, conforme orientação médica.",
        ],
      },
      {
        h: "Quando o resultado aparece",
        p: [
          "Parte do resultado é percebida após a retirada dos curativos, mas o edema leva tempo para diminuir. A ponta nasal costuma apresentar evolução mais lenta. O resultado deve ser avaliado de forma progressiva, e não apenas nas primeiras semanas.",
        ],
      },
      {
        h: "Naturalidade e limites",
        p: [
          "A rinoplastia precisa respeitar pele, cartilagens, ossos e função respiratória. Fotografias de referência ajudam a comunicar preferências, mas não permitem reproduzir o nariz de outra pessoa. O planejamento busca equilíbrio com a face.",
        ],
      },
    ],
    related: [
      { label: "Mommy makeover", to: P("mommy-makeover-foz-do-iguacu") },
      { label: "Lipoescultura", to: P("lipoescultura-foz-do-iguacu") },
      { label: "Mamoplastia de aumento", to: P("mamoplastia-de-aumento-foz-do-iguacu") },
    ],
    articles: [
      { label: "Rinoplastia: entenda o procedimento", to: "/rinoplastia-foz" },
      { label: "Rinoplastia em 2024: busca pelo resultado natural", to: "/rinoplastia-em-2024-busca-pelo-resultado-natural" },
      { label: "Os perigos de buscar o corpo perfeito do Instagram", to: "/os-perigos-de-se-buscar-na-cirurgia-plastica-o-corpo-perfeito-do-instagram" },
    ],
    faq: [
      {
        q: "Quanto tempo fico com curativo?",
        a: "O período depende da **técnica** e da evolução. A orientação é fornecida no planejamento e nos retornos.",
      },
      {
        q: "Quando o resultado fica pronto?",
        a: "O nariz muda gradualmente por vários meses. A ponta pode demorar mais para desinchar e definir.",
      },
      {
        q: "Quanto custa a rinoplastia?",
        a: "O valor depende da complexidade, da necessidade funcional, da estrutura e da equipe. É definido após **consulta**.",
      },
    ],
  },
];

export const getProcedure = (slug: string) =>
  PROCEDURES.find((p) => p.slug === slug) as ProcedureContent;
